import { Attributor } from 'parchment';
import { Mention, MentionBlot } from 'quill-mention';
import 'quill-mention/dist/quill.mention.min.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';

import DisplayStyle from './DisplayStyle';
import IndentStyle from './IndentStyle';
import MarginStyle from './MarginStyle';
import styles from './RichEditor.module.css';
import Toolbar, { ToolbarProps } from './Toolbar';

interface MentionType {
  id: number;
  value: string;
}

interface RichEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  image?: boolean;
  video?: boolean;
  variables?: ToolbarProps['variables'];
  readOnly?: boolean;
  mentions?: MentionType[];
}

//FIXME: Ao atualizar typescript importar de quill/formats/video
interface VideoType {
  new (): {
    domNode: HTMLVideoElement;
    format(name: string, value: string): void;
    html(): string;
    value(): { video: string };
  };
  blotName: string;
  className: string;
  tagName: string;
  create(value: string): Element;
  formats(domNode: Element): Record<string, string | null>;
  sanitize(url: string): string;
  value(domNode: Element): string;
}

const Video = Quill.import('formats/video') as VideoType;

class VideoEnhanced extends Video {
  static create(value: string) {
    const node = super.create(value);
    node.setAttribute('width', '640');
    node.setAttribute('height', '360');
    return node;
  }

  format(name: string, value: string) {
    if (name === 'align') {
      if (!value || value === 'justify') {
        this.domNode.setAttribute('style', 'display: block; margin: 0 auto 0 0');
      } else if (value === 'center') {
        this.domNode.setAttribute('style', 'display: block; margin: 0 auto');
      } else if (value === 'right') {
        this.domNode.setAttribute('style', 'display: block; margin: 0 0 0 auto');
      }
    } else {
      super.format(name, value);
    }
  }
}
Quill.register('formats/video', VideoEnhanced, true);

export const quillGetHTMLString = (message: string) => {
  // If the message is already HTML formatted, just return it
  // Simple regex to verify if there are any <p></p> HTML tags in the string (quill adds <p></p> HTML tags when formatting)
  if (/<p(.*?)>(.*?)<\/p>/g.test(message)) {
    return message;
  }
  // If the message is not HTML formatted, pass it through the editor to format it
  const tempContainer = document.createElement('div');
  const editor = new Quill(tempContainer);
  editor.setContents(editor.clipboard.convert({ html: message }));
  return tempContainer.getElementsByClassName('ql-editor')[0].innerHTML;
};

// Imports and registers so that the text editor saves styles as inline styles in the HTML
const DirectionAttribute = Quill.import('attributors/attribute/direction') as Attributor;
Quill.register(DirectionAttribute, true);
const AlignClass = Quill.import('attributors/class/align') as Attributor;
Quill.register(AlignClass, true);
const BackgroundClass = Quill.import('attributors/class/background') as Attributor;
Quill.register(BackgroundClass, true);
const ColorClass = Quill.import('attributors/class/color') as Attributor;
Quill.register(ColorClass, true);
const DirectionClass = Quill.import('attributors/class/direction') as Attributor;
Quill.register(DirectionClass, true);
const FontClass = Quill.import('attributors/class/font') as Attributor;
Quill.register(FontClass, true);
const SizeClass = Quill.import('attributors/class/size') as Attributor;
Quill.register(SizeClass, true);
const AlignStyle = Quill.import('attributors/style/align') as Attributor;
Quill.register(AlignStyle, true);
const BackgroundStyle = Quill.import('attributors/style/background') as Attributor;
Quill.register(BackgroundStyle, true);
const ColorStyle = Quill.import('attributors/style/color') as Attributor;
Quill.register(ColorStyle, true);
const DirectionStyle = Quill.import('attributors/style/direction') as Attributor;
Quill.register(DirectionStyle, true);
const FontStyle = Quill.import('attributors/style/font') as Attributor;
Quill.register(FontStyle, true);
const SizeStyle = Quill.import('attributors/style/size') as Attributor;
Quill.register(SizeStyle, true);
Quill.register(IndentStyle, true);
Quill.register(MarginStyle, true);
Quill.register(DisplayStyle, true);
Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention });

const Delta = Quill.import('delta');

function matchIdentation(node: HTMLElement, delta: InstanceType<typeof Delta>) {
  const style: Partial<CSSStyleDeclaration> = node.style || {};
  const numberOfTabs = parseFloat(Array.from(style.textIndent || '0')[0]);
  if (numberOfTabs > 0) {
    // Removing first insert operation containing a '\t' character
    delta = new Delta(delta.filter((op) => op.insert !== '\t'));

    // Correcting indent attribute to a number value instead of a string value
    delta = new Delta(
      delta.map((op) => {
        if (op.attributes && 'indent' in op.attributes) {
          op.attributes['indent'] = numberOfTabs;
        }
        return op;
      })
    );
  }
  return delta;
}

interface MentionModule {
  allowedChars: RegExp;
  mentionDenotationChars: string[];
  source: (
    searchTerm: string,
    renderList: (values: MentionType[], searchTerm: string) => void,
    mentionChar: string
  ) => void;
  positioningStrategy?: 'normal' | 'fixed';
}

const RichEditor = ({
  image = true,
  video = true,
  variables = [],
  readOnly,
  mentions,
  ...props
}: RichEditorProps) => {
  const quill = useRef<ReactQuill>(null);
  const toolbar = useMemo(() => Math.floor(Math.random() * 10_000), []); //FIXME: colocar um prefixo por prop para ajudar na ordenação quando tem muitos

  const [modules, setModules] = useState<{
    toolbar?: { container: string };
    clipboard: { matchers: [[number, Function]] };
  }>({
    clipboard: {
      matchers: [[Node.ELEMENT_NODE, matchIdentation]],
    },
  });

  // Destroy quill-mention selector when RichEditor is destroyed to avoid
  // a permanent quill-mention list on the screen
  useEffect(() => {
    return () => {
      const elements = document.getElementsByClassName('ql-mention-list-container');

      if (elements.length > 0 && elements[0].parentNode) {
        elements[0].parentNode.removeChild(elements[0]);
      }
    };
  }, []);

  const mentionsJson = JSON.stringify(mentions);

  const mentionModule: MentionModule = useMemo(
    () => ({
      allowedChars: /^[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ.\-\s]*$/,
      mentionDenotationChars: ['@'],
      positioningStrategy: 'fixed',
      source: (searchTerm, renderList, _) => {
        const mentions = JSON.parse(mentionsJson) as MentionType[] | undefined;

        if (!mentions) {
          return;
        }

        renderList(
          mentions.filter(({ value }) => value.toLowerCase().includes(searchTerm.toLowerCase())),
          searchTerm
        );
      },
    }),
    [mentionsJson]
  );

  useEffect(() => {
    setModules({
      toolbar: {
        container: `#toolbar-${toolbar}`,
      },
      clipboard: {
        matchers: [[Node.ELEMENT_NODE, matchIdentation]],
      },
    });
  }, [toolbar]);

  return (
    <div className={[styles.editor, 'notranslate'].join(' ')}>
      <Toolbar
        id={`toolbar-${toolbar}`}
        image={image}
        video={video}
        variables={variables}
        onInsertVariable={(variable) => {
          if (!quill.current) {
            return;
          }

          const editor = quill.current.getEditor();

          const selection = editor.getSelection(true);

          if (variable.value === '[link_perguntas_adicionais]') {
            editor.insertText(selection.index, 'Responder perguntas de pré-requisito', 'user');
            editor.setSelection(
              selection.index,
              'Responder perguntas de pré-requisito'.length,
              'user'
            );
            editor.format('link', variable.value, 'user');
            editor.setSelection(
              selection.index + 'Responder perguntas de pré-requisito'.length,
              0,
              'user'
            );
          } else {
            editor.insertText(selection.index, variable.value, 'user');
          }
        }}
        style={{ display: readOnly ? 'none' : undefined }}
      />
      {modules.toolbar && (
        <ReactQuill
          ref={quill}
          modules={{
            ...modules,
            ...(mentions && mentions.length > 0 ? { mention: mentionModule } : {}),
          }}
          readOnly={readOnly}
          theme="snow"
          preserveWhitespace
          {...props}
          onChange={(newValue: string, _: unknown, source: string) => {
            if (source !== 'api') {
              return props.onChange?.(newValue);
            }
          }}
        />
      )}
    </div>
  );
};

export default RichEditor;
